import { MouseEvent, useMemo } from 'react';

import { apiGetShortUrl } from 'api/shortUrl/apiShortUrl';
import { ApiShortUrlType } from 'api/shortUrl/types/ApiShortUrlType';
import { Box } from 'components/Box';
import { Icon, IconName } from 'components/Icon';
import { trackClicked } from 'utils/analytics/track/trackClicked';

import { ShareStyleButton } from './ShareStyleButton';
import { getSocialLink, makeParameters, withSocialUtm } from './helpers';

const socialWindowFeatures =
  'scrollbars=yes,menubar=no,width=700,height=600,resizable=yes,toolbar=no,location=no,status=no';

type SocialType = 'facebook' | 'twitter' | 'email' | 'linkedin';

const IconNameBySocialType: Record<
  'facebook' | 'twitter' | 'linkedin',
  IconName
> = {
  facebook: 'social/facebook',
  twitter: 'social/twitter',
  linkedin: 'social/linkedin',
};

type Props = {
  eventName: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventProperties?: Record<string, any>;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  object: Record<string, any>;
  objectType?: ApiShortUrlType;
  socialType: SocialType;
  utmParams: Record<string, string>;
  message?: string;
  subject?: string;
  onClick?: () => void;
  width?: number;
  height?: number;
};

const LABELS = {
  facebook: getText('Share on Facebook'),
  twitter: getText('Share on Twitter'),
  email: getText('Share by e-mail'),
  linkedin: getText('Share on LinkedIn'),
};

export function ShareButton({
  eventName,
  eventProperties,
  object,
  objectType,
  socialType,
  utmParams,
  message,
  subject,
  onClick,
  ...props
}: Props) {
  const handleClick = (e: MouseEvent) => {
    e.preventDefault();
    trackClicked(eventName, eventProperties || {});
    let linkPs: Promise<string>;
    let popup: Window;

    const isIosMobileApp = window.navigator.userAgent.includes(
      'Idealist Mobile App iOS',
    );

    if (!isIosMobileApp) {
      // We have to create the window before fetching the shorturl to prevent
      // iOS web Safari from blocking it.
      // @ts-expect-error TS(2322): Type 'Window | null' is not assignable to type 'Wi... Remove this comment to see the full error message
      popup = window.open('', `${socialType}-share`, socialWindowFeatures);
    }

    if (objectType) {
      linkPs = apiGetShortUrl({
        toType: objectType,
        toId: object.id,
        toUrl: object.url ? object.url[CURRENT_LOCALE] : null,
        extraParams: makeParameters(withSocialUtm(utmParams, socialType)),
      });
    } else {
      // Only use link shortener for Idealist links
      linkPs = object.url.startsWith('/')
        ? apiGetShortUrl({
            toType: 'URL',
            toUrl: object.url,
            extraParams: makeParameters(withSocialUtm(utmParams, socialType)),
          }) // No shortening for links that don't start with '/'
        : Promise.resolve(object.url);
    }

    linkPs.then((shortResourceLink) => {
      const link = getSocialLink(
        socialType,
        shortResourceLink,
        message,
        objectType,
        subject,
      );

      if (isIosMobileApp) {
        window.location.href = link;
      } else {
        popup.location.href = link;
        setTimeout(() => {
          if (socialType === 'email' && popup.location.href === 'about:blank') {
            popup.close();
          }
        }, 1000);
      }
    });

    if (onClick) {
      onClick();
    }
  };

  const url =
    (objectType === 'CMS_PAGE' ? object.url : object.url[CURRENT_LOCALE]) || '';

  const href = useMemo(() => {
    if (!object.url) return '';

    return getSocialLink(
      socialType,
      objectType
        ? `https://idealist.org${url.startsWith('/') ? '' : '/'}${url}`
        : object.url,
      message,
      objectType,
      subject,
    );
  }, [message, object.url, objectType, socialType, url, subject]);

  return (
    <Box
      onClick={handleClick}
      display="flex"
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      <ShareStyleButton
        data-qa-id={`social-button-${socialType}`}
        href={href}
        target="_blank"
        tabIndex={0}
        aria-label={LABELS[socialType]}
        title={LABELS[socialType]}
      >
        {socialType === 'email' ? (
          <Icon
            name="mail"
            keepColorOnLoad
            initialColorName="white"
            size={25}
          />
        ) : (
          <Icon
            name={IconNameBySocialType[socialType]}
            initialColorName="white"
            keepColorOnLoad
            size={25}
          />
        )}
      </ShareStyleButton>
    </Box>
  );
}
